
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import Swal, { SweetAlertIcon, SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import {isPlatformBrowser} from '@angular/common';

@Injectable({ providedIn: 'root' })
export class SweetAlertService {
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Generic fire (in case you need custom variations beyond success/error)
   */
  fire(options: SweetAlertOptions): Promise<SweetAlertResult<any>> {
    return Swal.fire(options);
  }

  /**
   * Standard success dialog (as requested)
   */
  success(
    title: string = 'Success',
    text: string = 'Your form has been submitted.',
    confirmButtonText: string = 'OK'
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title,
      text,
      icon: 'success',
      confirmButtonText,
    });
  }


  /**
   * Success dialog with HTML content (title + rich body)
   */
  successHtml(
    title: string = 'Success',
    html: string = '<p>Your form has been submitted.</p>',
    confirmButtonText: string = 'OK'
  ) {
    if (!this.isBrowser()) {
      return Promise.resolve();
    }
    return Swal.fire({
      icon: 'success',
      title,
      html: html,
      confirmButtonText: confirmButtonText,
    });
  }

  /**
   * Standard error dialog (as requested)
   */
  error(
    title: string = 'Error',
    text: string = 'Some fields in the form are invalid. Please review and try again, or contact our support team for assistance.',
    confirmButtonText: string = 'OK'
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title,
      text,
      icon: 'error',
      confirmButtonText,
    });
  }

  errorHtml(title: string, htmlMessage: string, confirmText = 'OK') {
    if (!this.isBrowser()) {
      // Server: no-op (or log). Avoid calling Swal/fire here.
      // console.warn('[SweetAlertService] errorHtml called on server; skipping UI');
      return Promise.resolve();
    }

    const decoded = this.decodeHtmlEntities(htmlMessage);
    // console.log("decoded", [decoded]);
    return Swal.fire({
      icon: 'error',
      title,
      html: decoded,
      confirmButtonText: confirmText,
    });
  }


  /**
   * Optional: minimal API for other icons while keeping a unified style
   */
  info(
    title: string,
    text?: string,
    confirmButtonText: string = 'OK'
  ) {
    return Swal.fire({
      title,
      text,
      icon: 'info',
      confirmButtonText
    });
  }

  infoHtml(title: string, htmlMessage: string, confirmText = 'OK'
  ) {
    const decoded = this.decodeHtmlEntities(htmlMessage);
    // console.log("decoded", [decoded]);
    return Swal.fire({
      icon: 'info',
      title,
      html: decoded,
      confirmButtonText: confirmText,
    });
  }

  warning(
    title: string,
    text?: string,
    confirmButtonText: string = 'OK'
  ) {
    return Swal.fire({
      title,
      text, icon: 'warning',
      confirmButtonText
    });
  }

  /**
   * Optional confirm helper (common UX pattern)
   */
  confirm(
    title: string,
    text: string,
    confirmButtonText: string = 'Yes',
    cancelButtonText: string = 'Cancel',
    icon: SweetAlertIcon = 'warning'
  ) {
    return Swal.fire({
      title,
      text,
      icon,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      reverseButtons: true
    });
  }

  // Only use DOM in the browser
  private decodeHtmlEntities(input: string): string {
    if (!this.isBrowser()) {
      // On the server, just return original (or do a light non-DOM decode if needed)
      return input ?? '';
    }
    const el = document.createElement('textarea');
    el.innerHTML = input ?? '';
    return el.value;
  }

}
